import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { type Media, useFetchMediasForCase } from "@/queries/useFetchMediaForCase.ts";
import { apiUrl } from "@/utils/api.ts";
import {
    generateSettlementStatementDocx,
    generateSettlementStatementFilename,
} from "@/utils/settlementStatementDocxGenerator.ts";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { ChevronDown, Download, FileText } from "lucide-react";
import { useSnackbar } from "notistack";
import { PDFDocument } from "pdf-lib";
import { useMemo, useState } from "react";

const StyledDialog = styled(Dialog)(() => ({
    "& .MuiDialog-paper": {
        maxWidth: 700,
        width: "100%",
    },
}));

const CategoryAccordion = styled(Accordion)(({ theme }) => ({
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    "&:before": {
        display: "none",
    },
    "&.Mui-expanded": {
        margin: "8px 0",
    },
}));

const DocumentListItem = styled(ListItem)(({ theme }) => ({
    paddingLeft: theme.spacing(4),
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
}));

interface DocumentCategory {
    name: string;
    documents: Media[];
    defaultExpanded: boolean;
}

interface DisbursementRow {
    idUUID: string;
    company: string;
    accountNumber?: string | null;
    type: string;
    bill: string | number;
    insurance: string | number;
    adj: string | number;
    self: string | number;
    medPay: string | number;
    currentBalance: string | number;
    reduction: string | number;
    source: "provider" | "loan" | "insurance";
}

interface CostData {
    bill: string;
    insurance: string;
    adj: string;
    self: string;
    medPay: string;
    currentBalance: string;
    reduction: string;
    sent: string;
    approved: string;
}

interface DisbursementData {
    negotiatedLiability: number;
    negotiatedUIUIM: number;
    totalNegotiated: number;
    rows: DisbursementRow[];
    costData: CostData;
    attorneyFeePercentage: number;
    attorneyFeeAmount: number;
    clientDistributionAmount: number;
}

interface SettlementStatementModalProps {
    open: boolean;
    onClose: () => void;
    caseNumber: string;
    clientName: string;
    caseIdUUID: string;
    disbursementData: DisbursementData;
    dateOfLoss?: string;
}

const SettlementStatementModal = ({
    open,
    onClose,
    caseNumber,
    clientName,
    caseIdUUID,
    disbursementData,
    dateOfLoss = "",
}: SettlementStatementModalProps) => {
    const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
    const [isGenerating, setIsGenerating] = useState(false);
    const [isGeneratingWord, setIsGeneratingWord] = useState(false);

    // Hook must be called at top level of component
    const authenticatedFetch = useAuthenticatedFetch();
    const { enqueueSnackbar } = useSnackbar();

    // Fetch real media/documents for the case
    const { data: media, isLoading: isLoadingMedia } = useFetchMediasForCase({
        caseIdUUID,
    });

    // Categorize documents based on tags
    const documentCategories: DocumentCategory[] = useMemo(() => {
        if (!media) {
            return [];
        }

        // Define categories and their matching tags
        const categoryDefinitions = [
            {
                name: "Providers",
                tags: ["Providers", "Provider"],
                defaultExpanded: true,
            },
            {
                name: "Disbursements",
                tags: ["Disbursement"],
                defaultExpanded: true,
            },
            {
                name: "Medical Records",
                tags: ["Medical", "Lien", "UM/UIM"],
                defaultExpanded: false,
            },
            {
                name: "Correspondence",
                tags: ["Correspondence", "Negotiation", "Liability"],
                defaultExpanded: false,
            },
        ];

        // Track which documents have been assigned to a category
        const assignedDocIds = new Set<string>();

        // Build categories with their documents
        const categories = categoryDefinitions.map((catDef) => {
            const documents = media.filter((doc) => {
                if (!doc.tag) {
                    return false;
                }
                const matches = catDef.tags.some((tag) => doc.tag?.includes(tag));
                if (matches) {
                    assignedDocIds.add(doc.idUUID);
                }
                return matches;
            });

            return {
                name: catDef.name,
                documents,
                defaultExpanded: catDef.defaultExpanded,
            };
        });

        // Add "General" category for all remaining documents not in other categories
        const generalDocuments = media.filter((doc) => !assignedDocIds.has(doc.idUUID));

        if (generalDocuments.length > 0) {
            categories.push({
                name: "General",
                documents: generalDocuments,
                defaultExpanded: false,
            });
        }

        // Filter out empty categories
        return categories.filter((cat) => cat.documents.length > 0);
    }, [media]);

    const handleDocumentToggle = (documentId: string) => {
        const newSelected = new Set(selectedDocuments);
        if (newSelected.has(documentId)) {
            newSelected.delete(documentId);
        } else {
            newSelected.add(documentId);
        }
        setSelectedDocuments(newSelected);
    };

    const handleCategoryToggle = (category: DocumentCategory) => {
        const categoryDocIds = category.documents.map((doc) => doc.idUUID);
        const allSelected = categoryDocIds.every((id) => selectedDocuments.has(id));

        const newSelected = new Set(selectedDocuments);
        if (allSelected) {
            categoryDocIds.forEach((id) => newSelected.delete(id));
        } else {
            categoryDocIds.forEach((id) => newSelected.add(id));
        }
        setSelectedDocuments(newSelected);
    };

    const generateSettlementStatementPDF = (): Uint8Array => {
        // Create the settlement statement PDF
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let yPosition = 20;

        // Helper function to format currency
        const formatCurrency = (value: string | number): string => {
            if (typeof value === "string") {
                // If already formatted, return as-is
                if (value.startsWith("$")) {
                    return value;
                }
                const num = Number.parseFloat(value);
                return Number.isNaN(num) ? "$0.00" : `$${num.toFixed(2)}`;
            }
            return `$${value.toFixed(2)}`;
        };

        // Helper to check if new page needed
        const checkNewPage = (requiredSpace: number) => {
            if (yPosition + requiredSpace > pageHeight - 20) {
                doc.addPage();
                yPosition = 20;
                return true;
            }
            return false;
        };

        // Header
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text("SETTLEMENT STATEMENT", pageWidth / 2, yPosition, { align: "center" });

        yPosition += 10;
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text(`Case: ${caseNumber}`, pageWidth / 2, yPosition, { align: "center" });

        yPosition += 6;
        doc.text(`Client: ${clientName}`, pageWidth / 2, yPosition, { align: "center" });

        yPosition += 6;
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, {
            align: "center",
        });

        yPosition += 15;

        // Negotiated Amounts Section
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Negotiated Settlement Amounts", 14, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("Negotiated Liability:", 20, yPosition);
        doc.text(formatCurrency(disbursementData.negotiatedLiability), 120, yPosition, {
            align: "right",
        });
        yPosition += 6;

        doc.text("Negotiated UI/UIM:", 20, yPosition);
        doc.text(formatCurrency(disbursementData.negotiatedUIUIM), 120, yPosition, {
            align: "right",
        });
        yPosition += 6;

        doc.setFont("helvetica", "bold");
        doc.text("Total Negotiated:", 20, yPosition);
        doc.text(formatCurrency(disbursementData.totalNegotiated), 120, yPosition, {
            align: "right",
        });
        yPosition += 12;

        // Disbursements Table
        checkNewPage(40);
        doc.setFontSize(12);
        doc.text("Disbursements", 14, yPosition);
        yPosition += 8;

        // Table headers
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        const colX = {
            company: 14,
            type: 70,
            bill: 95,
            reduction: 125,
            balance: 155,
        };

        doc.text("Company/Provider", colX.company, yPosition);
        doc.text("Type", colX.type, yPosition);
        doc.text("Bill", colX.bill, yPosition);
        doc.text("Reduction", colX.reduction, yPosition);
        doc.text("Balance", colX.balance, yPosition);
        yPosition += 5;

        doc.setFont("helvetica", "normal");

        // Provider rows only (not loans)
        const providerRows = disbursementData.rows.filter((r) => r.source === "provider");
        providerRows.forEach((row) => {
            checkNewPage(10);
            const companyText = row.accountNumber
                ? `${row.company}\n${row.accountNumber}`
                : row.company;
            doc.text(companyText, colX.company, yPosition, { maxWidth: 50 });
            doc.text(row.type || "", colX.type, yPosition);
            doc.text(formatCurrency(row.bill), colX.bill, yPosition);
            doc.text(formatCurrency(row.reduction), colX.reduction, yPosition);
            doc.text(formatCurrency(row.currentBalance), colX.balance, yPosition);
            yPosition += row.accountNumber ? 10 : 6;
        });

        // Provider Subtotals
        checkNewPage(10);
        doc.setFont("helvetica", "bold");
        const providerBillTotal = providerRows.reduce(
            (sum, r) => sum + Number.parseFloat(String(r.bill).replace("$", "")),
            0,
        );
        const providerReductionTotal = providerRows.reduce(
            (sum, r) => sum + Number.parseFloat(String(r.reduction).replace("$", "")),
            0,
        );
        doc.text("Provider Subtotals:", colX.company, yPosition);
        doc.text(formatCurrency(providerBillTotal), colX.bill, yPosition);
        doc.text(formatCurrency(providerReductionTotal), colX.reduction, yPosition);
        yPosition += 8;
        doc.setFont("helvetica", "normal");

        // Loan rows (below Provider Subtotals per CAM-364 AC4)
        const loanRows = disbursementData.rows.filter((r) => r.source === "loan");
        loanRows.forEach((row) => {
            checkNewPage(10);
            const companyText = row.accountNumber
                ? `${row.company}\nID#: ${row.accountNumber}`
                : row.company;
            doc.text(companyText, colX.company, yPosition, { maxWidth: 50 });
            doc.text(row.type || "Loan", colX.type, yPosition);
            doc.text(formatCurrency(row.bill), colX.bill, yPosition);
            doc.text(formatCurrency(row.reduction), colX.reduction, yPosition);
            doc.text(formatCurrency(row.currentBalance), colX.balance, yPosition);
            yPosition += row.accountNumber ? 10 : 6;
        });

        // Insurance Companies
        const insuranceRows = disbursementData.rows.filter((r) => r.source === "insurance");
        insuranceRows.forEach((row) => {
            checkNewPage(10);
            doc.text(row.company, colX.company, yPosition);
            doc.text(row.type || "Insurance", colX.type, yPosition);
            doc.text(formatCurrency(row.bill), colX.bill, yPosition);
            doc.text(formatCurrency(row.reduction), colX.reduction, yPosition);
            doc.text(formatCurrency(row.currentBalance), colX.balance, yPosition);
            yPosition += 6;
        });

        // COST Row (Yellow background)
        checkNewPage(10);
        doc.setFillColor(255, 243, 205); // Yellow
        doc.rect(12, yPosition - 4, pageWidth - 24, 8, "F");
        doc.setFont("helvetica", "bold");
        doc.text("COST", colX.company, yPosition);
        doc.text(formatCurrency(disbursementData.costData.bill), colX.bill, yPosition);
        doc.text(formatCurrency(disbursementData.costData.reduction), colX.reduction, yPosition);
        doc.text(formatCurrency(disbursementData.costData.currentBalance), colX.balance, yPosition);
        yPosition += 8;

        // Attorney Fee Row (Blue background)
        checkNewPage(10);
        doc.setFillColor(207, 226, 255); // Blue
        doc.rect(12, yPosition - 4, pageWidth - 24, 8, "F");
        doc.text(
            `Attorney Fee (${disbursementData.attorneyFeePercentage}%)`,
            colX.company,
            yPosition,
        );
        doc.text(formatCurrency(disbursementData.attorneyFeeAmount), colX.bill, yPosition);
        doc.text(formatCurrency(disbursementData.attorneyFeeAmount), colX.reduction, yPosition);
        yPosition += 8;

        // Client Distribution Row (Green background)
        checkNewPage(10);
        doc.setFillColor(209, 231, 221); // Green
        doc.rect(12, yPosition - 4, pageWidth - 24, 8, "F");
        doc.text("Client Distribution", colX.company, yPosition);
        doc.text(formatCurrency(disbursementData.clientDistributionAmount), colX.bill, yPosition);
        doc.text(
            formatCurrency(disbursementData.clientDistributionAmount),
            colX.reduction,
            yPosition,
        );
        yPosition += 8;

        // Settlement Total Row (Gray background)
        checkNewPage(10);
        doc.setFillColor(224, 224, 224); // Gray
        doc.rect(12, yPosition - 4, pageWidth - 24, 8, "F");
        doc.text("SETTLEMENT TOTAL", colX.company, yPosition);
        doc.text(formatCurrency(disbursementData.totalNegotiated), colX.reduction, yPosition);
        yPosition += 12;

        // Attached Documents Section
        checkNewPage(30);
        doc.setFillColor(255, 255, 255); // Reset to white
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Attached Documents", 14, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        if (selectedDocuments.size > 0) {
            doc.text(
                `The following ${selectedDocuments.size} document(s) are attached:`,
                20,
                yPosition,
            );
            yPosition += 6;

            // List selected documents
            const allDocs = documentCategories.flatMap((cat) => cat.documents);
            const selectedDocs = allDocs.filter((doc) => selectedDocuments.has(doc.idUUID));

            selectedDocs.forEach((selectedDoc, index) => {
                checkNewPage(10);
                const displayName = selectedDoc.filename || "Unnamed Document";
                doc.text(`${index + 1}. ${displayName}`, 25, yPosition);
                yPosition += 5;
            });
        } else {
            doc.text("No additional documents attached.", 20, yPosition);
        }

        return doc.output("arraybuffer") as Uint8Array;
    };

    const handleGenerateStatement = async () => {
        setIsGenerating(true);

        try {
            // Create the merged PDF document
            const mergedPdf = await PDFDocument.create();

            // Generate and add the settlement statement cover page
            const statementBytes = generateSettlementStatementPDF();
            const statementPdf = await PDFDocument.load(statementBytes);
            const statementPages = await mergedPdf.copyPages(
                statementPdf,
                statementPdf.getPageIndices(),
            );
            statementPages.forEach((page) => mergedPdf.addPage(page));

            // Fetch and merge all selected documents
            const failedDocuments: string[] = [];
            if (selectedDocuments.size > 0) {
                const allDocs = documentCategories.flatMap((cat) => cat.documents);
                const selectedDocs = allDocs.filter((doc) => selectedDocuments.has(doc.idUUID));

                for (const doc of selectedDocs) {
                    try {
                        // Fetch the document blob from the API
                        const fetchURL = apiUrl("/s3/download-media");
                        fetchURL.searchParams.set("mediaUUID", doc.idUUID);

                        const response = await authenticatedFetch(fetchURL, {
                            method: "GET",
                        });

                        if (!response.ok) {
                            failedDocuments.push(doc.filename || "Unknown document");
                            continue;
                        }

                        const blob = await response.blob();
                        const arrayBuffer = await blob.arrayBuffer();

                        // Handle PDFs
                        if (blob.type === "application/pdf") {
                            const pdf = await PDFDocument.load(arrayBuffer);
                            const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
                            pages.forEach((page) => mergedPdf.addPage(page));
                        }
                        // Handle images (PNG, JPEG, etc.)
                        else if (blob.type.startsWith("image/")) {
                            const page = mergedPdf.addPage();
                            const { width, height } = page.getSize();

                            let image: Awaited<
                                ReturnType<typeof mergedPdf.embedPng | typeof mergedPdf.embedJpg>
                            >;
                            if (blob.type === "image/png") {
                                image = await mergedPdf.embedPng(arrayBuffer);
                            } else if (blob.type === "image/jpeg" || blob.type === "image/jpg") {
                                image = await mergedPdf.embedJpg(arrayBuffer);
                            } else {
                                // Unsupported image type - skip silently
                                continue;
                            }

                            // Scale image to fit page while maintaining aspect ratio
                            const imageAspectRatio = image.width / image.height;
                            const pageAspectRatio = width / height;

                            let scaledWidth = width;
                            let scaledHeight = height;

                            if (imageAspectRatio > pageAspectRatio) {
                                // Image is wider than page
                                scaledHeight = width / imageAspectRatio;
                            } else {
                                // Image is taller than page
                                scaledWidth = height * imageAspectRatio;
                            }

                            // Center the image on the page
                            const x = (width - scaledWidth) / 2;
                            const y = (height - scaledHeight) / 2;

                            page.drawImage(image, {
                                x,
                                y,
                                width: scaledWidth,
                                height: scaledHeight,
                            });
                        }
                    } catch (error) {
                        failedDocuments.push(doc.filename || "Unknown document");
                        // Continue with other documents even if one fails
                    }
                }
            }

            // Show notification if any documents failed
            if (failedDocuments.length > 0) {
                enqueueSnackbar(
                    `Warning: ${
                        failedDocuments.length
                    } document(s) could not be attached: ${failedDocuments.slice(0, 3).join(", ")}${
                        failedDocuments.length > 3 ? "..." : ""
                    }`,
                    { variant: "warning", autoHideDuration: 8000 },
                );
            }

            // Save and download the merged PDF
            const mergedPdfBytes = await mergedPdf.save();
            const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Settlement_Statement_${caseNumber.replace(/\s+/g, "_")}_${
                new Date().toISOString().split("T")[0]
            }.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            enqueueSnackbar(
                `Error generating PDF: ${
                    error instanceof Error ? error.message : "Unknown error"
                }. Please try again or contact support.`,
                { variant: "error", autoHideDuration: 10000 },
            );
        } finally {
            setIsGenerating(false);
            onClose();
        }
    };

    const handleDownloadWord = async () => {
        setIsGeneratingWord(true);

        try {
            // Get selected document names
            const allDocs = documentCategories.flatMap((cat) => cat.documents);
            const selectedDocs = allDocs.filter((doc) => selectedDocuments.has(doc.idUUID));
            const selectedDocumentNames = selectedDocs.map(
                (doc) => doc.filename || "Unnamed Document",
            );

            // Generate Word document
            const blob = await generateSettlementStatementDocx({
                clientName,
                caseNumber,
                dateOfLoss,
                negotiatedLiability: disbursementData.negotiatedLiability,
                negotiatedUIUIM: disbursementData.negotiatedUIUIM,
                attorneyFeePercentage: disbursementData.attorneyFeePercentage,
                attorneyFeeAmount: disbursementData.attorneyFeeAmount,
                disbursementRows: disbursementData.rows,
                costData: disbursementData.costData,
                clientDistributionAmount: disbursementData.clientDistributionAmount,
                selectedDocuments: selectedDocumentNames,
            });

            // Download the file
            const filename = generateSettlementStatementFilename(caseNumber);
            saveAs(blob, filename);

            onClose();
        } catch (error) {
            enqueueSnackbar(
                `Error generating Word document: ${
                    error instanceof Error ? error.message : "Unknown error"
                }. Please try again or contact support.`,
                { variant: "error", autoHideDuration: 10000 },
            );
        } finally {
            setIsGeneratingWord(false);
        }
    };

    const getSelectedCount = () => selectedDocuments.size;

    const isCategorySelected = (category: DocumentCategory) => {
        if (category.documents.length === 0) {
            return false;
        }
        return category.documents.every((doc) => selectedDocuments.has(doc.idUUID));
    };

    const isCategoryIndeterminate = (category: DocumentCategory) => {
        const selectedInCategory = category.documents.filter((doc) =>
            selectedDocuments.has(doc.idUUID),
        );
        return (
            selectedInCategory.length > 0 && selectedInCategory.length < category.documents.length
        );
    };

    // Format date
    const formatDate = (date: Date | null): string => {
        if (!date) {
            return "";
        }
        return new Date(date).toLocaleDateString();
    };

    return (
        <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Typography variant="h6" component="div">
                    Download Settlement Statement
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {clientName} - {caseNumber}
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                {isLoadingMedia ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Select documents to attach to the settlement statement. The
                                statement will be generated first, followed by the selected
                                documents in the order shown below.
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {getSelectedCount()} document{getSelectedCount() !== 1 ? "s" : ""}{" "}
                                selected
                            </Typography>
                        </Box>

                        {documentCategories.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: "center" }}>
                                <Typography variant="body2" color="text.secondary">
                                    No documents found for this case.
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                {documentCategories.map((category) => (
                                    <CategoryAccordion
                                        key={category.name}
                                        defaultExpanded={category.defaultExpanded}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ChevronDown size={20} />}
                                            sx={{
                                                "& .MuiAccordionSummary-content": {
                                                    alignItems: "center",
                                                    my: 1,
                                                },
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={isCategorySelected(category)}
                                                        indeterminate={isCategoryIndeterminate(
                                                            category,
                                                        )}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleCategoryToggle(category);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                }
                                                label={
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            fontWeight={500}
                                                        >
                                                            {category.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            ({category.documents.length})
                                                        </Typography>
                                                    </Box>
                                                }
                                                sx={{ m: 0 }}
                                            />
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <List disablePadding>
                                                {category.documents.map((doc, index) => (
                                                    <div key={doc.idUUID}>
                                                        {index > 0 && <Divider />}
                                                        <DocumentListItem>
                                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                                <Checkbox
                                                                    checked={selectedDocuments.has(
                                                                        doc.idUUID,
                                                                    )}
                                                                    onChange={() =>
                                                                        handleDocumentToggle(
                                                                            doc.idUUID,
                                                                        )
                                                                    }
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                                <FileText size={20} />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    doc.filename ||
                                                                    "Unnamed Document"
                                                                }
                                                                secondary={formatDate(
                                                                    doc.metaCreatedWhen,
                                                                )}
                                                                primaryTypographyProps={{
                                                                    variant: "body2",
                                                                }}
                                                                secondaryTypographyProps={{
                                                                    variant: "caption",
                                                                }}
                                                            />
                                                        </DocumentListItem>
                                                    </div>
                                                ))}
                                            </List>
                                        </AccordionDetails>
                                    </CategoryAccordion>
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isGenerating || isGeneratingWord}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={
                        isGenerating ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <Download size={16} />
                        )
                    }
                    onClick={handleGenerateStatement}
                    disabled={isGenerating || isGeneratingWord || isLoadingMedia}
                >
                    {isGenerating ? "Generating..." : "Download PDF"}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={
                        isGeneratingWord ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <Download size={16} />
                        )
                    }
                    onClick={handleDownloadWord}
                    disabled={isGenerating || isGeneratingWord || isLoadingMedia}
                >
                    {isGeneratingWord ? "Generating..." : "Download Word"}
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default SettlementStatementModal;
